import { ArrowRight, CalendarDays, Clock3, Heart, BookOpen, ShoppingCart, Gift } from "lucide-react";

const AMAZON_URL = "https://www.amazon.de/dp/B0HQBRVNPD";

/**
 * Promo-Block "Mein Pflegekalender 2027" · Kalender-Edition im Magazin-Look.
 * Jetzt bei Amazon erhältlich · sekundär: digitales Blätterbuch.
 */
export default function KalenderPromo({ testId = "kalender-promo" }) {
  const stats = [
    { icon: CalendarDays, label: "Tagesseiten", value: "365" },
    { icon: Clock3, label: "Dienstplanung", value: "F · S · N" },
    { icon: BookOpen, label: "Magazin-Look", value: "528 Seiten" },
    { icon: Heart, label: "Selbstfürsorge", value: "eingebaut" },
  ];

  return (
    <section data-testid={testId} className="rounded-3xl overflow-hidden relative bg-peach-50 border border-cream-300">
      <div className="relative p-7 sm:p-10 lg:p-12 lg:grid lg:grid-cols-[1fr_auto] lg:gap-12 lg:items-center">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span data-testid={`${testId}-badge`} className="inline-flex items-center rounded-full bg-terra text-white px-3.5 py-1 text-[10px] uppercase tracking-[0.22em] font-bold">
              Jetzt bei Amazon
            </span>
            <span data-testid={`${testId}-gift-badge`} className="inline-flex items-center gap-1.5 rounded-full bg-brand-darkred text-white px-3.5 py-1 text-[10px] uppercase tracking-[0.22em] font-bold">
              <Gift className="h-3 w-3" strokeWidth={2.2} /> Geschenkidee zu Weihnachten
            </span>
            <span className="text-[10.5px] uppercase tracking-[0.28em] font-semibold text-terra">
              Kalender-Edition 2027 · N° 01
            </span>
          </div>

          {/* Cover mobil sichtbar */}
          <div className="mt-6 lg:hidden">
            <img
              src="/kalender2027/cover.jpg"
              alt="Mein Pflegekalender 2027 – Cover"
              className="w-44 aspect-[7/10] object-cover shadow-[0_22px_45px_-18px_rgba(58,52,46,0.45)]"
              loading="lazy"
            />
          </div>

          <h3 className="mt-5 font-display leading-[1.1] text-2xl sm:text-3xl lg:text-[2.2rem] text-sand-800">
            Mein Pflegekalender 2027. <span className="italic block sm:inline text-brand-darkred">Der Schichtplaner, der auch an dich denkt.</span>
          </h3>
          <p className="mt-5 leading-[1.65] text-[14.5px] sm:text-[15.5px] max-w-2xl text-sand-700">
            Früh, Spät, Nacht – und mittendrin dein Leben. Eine eigene Seite für jeden Tag, Überstunden-
            und Urlaubskonto, Wunschdienstpläne und jeden Monat ein Editorial zum Durchatmen.
            Gestaltet wie unser Magazin: cremefarbenes Papier, warme Fotografie, Kupfer-Details.
            <span className="block mt-2 font-semibold text-brand-darkred">
              Das Weihnachtsgeschenk für Pflegekräfte – für Kolleginnen, dein Team oder dich selbst.
            </span>
          </p>

          <ul className="mt-7 grid grid-cols-2 lg:grid-cols-4 gap-3">
            {stats.map(({ icon: Icon, label, value }) => (
              <li key={label} className="rounded-2xl px-4 py-3 bg-white border border-cream-300">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-terra">
                  <Icon className="h-3 w-3" strokeWidth={2} /> {label}
                </div>
                <div className="mt-1 font-display text-lg sm:text-xl text-sand-800">{value}</div>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href={AMAZON_URL} target="_blank" rel="noopener" data-testid={`${testId}-amazon`}
              className="inline-flex items-center gap-2 rounded-full px-6 sm:px-7 h-12 text-[13.5px] sm:text-sm font-semibold transition-colors bg-terra hover:bg-terra-dark text-white">
              <ShoppingCart className="h-4 w-4" strokeWidth={2} /> Jetzt bei Amazon kaufen (42,69 €)
            </a>
            <a href="/kalender-blaettern.html" target="_blank" rel="noopener" data-testid={`${testId}-cta`}
              className="inline-flex items-center gap-2 rounded-full px-6 sm:px-7 h-12 text-[13.5px] sm:text-sm font-semibold transition-colors border border-terra text-terra hover:bg-terra hover:text-white">
              Digital durchblättern <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </a>
            <span data-testid={`${testId}-preis`} className="text-[12.5px] text-sand-600">
              Taschenbuch · 17,8 × 25,4 cm · 528 Seiten
            </span>
          </div>
        </div>

        {/* Cover Desktop */}
        <div className="hidden lg:block shrink-0">
          <div className="relative">
            <img
              src="/kalender2027/cover.jpg"
              alt="Mein Pflegekalender 2027 – Cover der Kalender-Edition"
              className="w-56 xl:w-64 aspect-[7/10] object-cover shadow-[0_22px_45px_-18px_rgba(58,52,46,0.45)]"
              loading="lazy"
            />
            <div className="absolute -bottom-3 -right-3 h-full w-full border border-saffron-dark/50 pointer-events-none" aria-hidden="true" />
          </div>
          <div className="mt-4 text-center text-[9.5px] uppercase tracking-[0.26em] font-bold text-terra">
            365 Tage · Jetzt bei Amazon
          </div>
        </div>
      </div>
    </section>
  );
}
